import React from 'react';
import { connect } from 'react-redux';
import { ResponsiveImage, ResponsiveImageSize } from 'react-responsive-image';
import actions from './../actions/small-card-actions';
import styleHelper from './../helpers/style';


/**
 * Slideshow component
 * @class
 */
class Slideshow extends React.Component {
  constructor(props) {
    super(props);


    /**
     * Component display name used by React
     */
    this.displayName = 'Slideshow';


    /**
     * setinterval to advance slides
     */
    this.advanceInterval = 0;


    /**
     * setTimeout to change slides
     */
    this.advanceTimeout = 0;


    /**
     * Time between each slide
     */
    this.timeBetweenSlides = 4000;


    // bind functions to component
    this.renderSlides = this.renderSlides.bind(this);
    this.onFirstImageLoaded = this.onFirstImageLoaded.bind(this);
    this.transitionSlide = this.transitionSlide.bind(this);
    this.advanceSlide = this.advanceSlide.bind(this);
    this.stopSlideshow = this.stopSlideshow.bind(this);
    this.startSlideshow = this.startSlideshow.bind(this);
  }


  /**
   * Called before component is removed from the DOM
   * @returns {undefined} undefined
   */
  componentWillUnmount() {
    this.stopSlideshow();
  }


  /**
   * Called when component props or state updates
   * @param {Object} prevProps - The previous props
   * @returns {undefined} undefined
   */
  componentDidUpdate(prevProps) {
    if (this.props.active && !prevProps.active) {
      this.startSlideshow();
    }

    if (!this.props.active && prevProps.active) {
      this.stopSlideshow();
    }
  }


  /**
   * Stops the slideshow
   * @returns {undefined} undefined
   */
  stopSlideshow() {
    clearInterval(this.advanceInterval);
    clearTimeout(this.advanceTimeout);
  }


  /**
   * Starts the slideshow
   * @returns {undefined} undefined
   */
  startSlideshow() {
    this.advanceInterval =
      setInterval(this.transitionSlide, this.timeBetweenSlides);
  }


  /**
   * Renders slides
   * @returns {Array} - An array of React elements
   */
  renderSlides() {
    const state = this.props.slideshowState;
    const globalState = this.props.mediaLabCardFrameworkState;
    const data = this.props.data;
    const slides = [];
    const windowSize = globalState.windowSize;
    let activeSlide = 0;
    let nextSlide = 1;
    if (state.slideshows[this.props.id] &&
      state.slideshows[this.props.id].activeSlide) {
      activeSlide = state.slideshows[this.props.id].activeSlide;
      nextSlide = activeSlide + 1;
    }
    if (nextSlide > this.props.data.length - 1) {
      nextSlide = 0;
    }

    const currentImage1Size = (windowSize.width < this.props.largeBreakpoint) ?
      data[nextSlide].size : data[nextSlide].largeSize;
    const image1Style = styleHelper.getSizeToCover(
      currentImage1Size.width,
      currentImage1Size.height,
      windowSize.width,
      windowSize.height
    );

    const currentImage2Size = (windowSize.width < this.props.largeBreakpoint) ?
      data[activeSlide].size : data[activeSlide].largeSize;
    const image2Style = styleHelper.getSizeToCover(
      currentImage2Size.width,
      currentImage2Size.height,
      windowSize.width,
      windowSize.height
    );

    slides.push(
      <ResponsiveImage
        className='left'
        imageStyle={image1Style}
        key={'fade-slideshow-slide-' + this.props.id + '-' + data[nextSlide].id}
        ref='image'
      >
        <ResponsiveImageSize
          default
          minWidth={0}
          path={data[nextSlide].path}
        />
        <ResponsiveImageSize
          minWidth={768}
          path={data[nextSlide].largePath}
        />
      </ResponsiveImage>
    );

    slides.push(
      <ResponsiveImage
        className='current'
        imageStyle={image2Style}
        key={'fade-slideshow-slide-' + this.props.id + '-' + data[activeSlide].id}
        ref='image'
        onLoad={this.onFirstImageLoaded}
      >
        <ResponsiveImageSize
          default
          minWidth={0}
          path={data[activeSlide].path}
        />
        <ResponsiveImageSize
          minWidth={768}
          path={data[activeSlide].largePath}
        />
      </ResponsiveImage>
    );

    return slides;
  }


  /**
   * Called when the first image is loaded for the first time
   * @returns {undefined} undefined
   */
  onFirstImageLoaded() {
    const state = this.props.slideshowState;

    if (this.props.active && (!state.slideshows[this.props.id] ||
      !state.slideshows[this.props.id].firstSlideLoaded)) {
      this.props.dispatch(actions.setSlideshowLoading(this.props.id, false));
      this.props.dispatch(actions.setSlideshowFirstSlideLoaded(this.props.id, true));

      setTimeout(() => {
        if (this.props.firstSlideLoadCallback) {
          this.props.firstSlideLoadCallback();
        }

        this.advanceInterval =
          setInterval(this.transitionSlide, this.timeBetweenSlides);
      }, 10);
    }
  }


  /**
   * Does the transition animation to the next slide
   * @returns {undefined} undefined
   */
  transitionSlide() {
    this.props.dispatch(actions.setSlideshowTransitioning(this.props.id, true));

    this.advanceTimeout = setTimeout(() => {
      this.props.dispatch(actions.setSlideshowLoading(this.props.id, true));
      this.props.dispatch(actions.setSlideshowTransitioning(this.props.id, false));

      setTimeout(() => {
        this.advanceSlide();
      }, 10);
    }, 1000);
  }


  /**
   * Sets the current and next slides
   * @returns {undefined} undefined
   */
  advanceSlide() {
    const state = this.props.slideshowState;
    let activeSlide = 0;
    if (state.slideshows[this.props.id] &&
      state.slideshows[this.props.id].activeSlide) {
      activeSlide = state.slideshows[this.props.id].activeSlide;
    }

    let nextSlide = activeSlide + 1;
    if (nextSlide > this.props.data.length - 1) {
      nextSlide = 0;
    }

    this.props.dispatch(
      actions.setSlideshowActiveSlide(this.props.id, nextSlide));
    this.props.dispatch(actions.setSlideshowLoading(this.props.id, false));
  }


  /**
   * Renders component
   * @returns {Object} React element
   */
  render() {
    const slides = this.renderSlides();
    const state = this.props.slideshowState;
    let firstSlideLoadedClass = '';
    let loadingClass = '';
    let transitioningClass = '';

    if (state.slideshows[this.props.id] &&
      state.slideshows[this.props.id].firstSlideLoaded) {
      firstSlideLoadedClass = ' first-slide-loaded';
    }

    if (state.slideshows[this.props.id] &&
      state.slideshows[this.props.id].loading) {
      loadingClass = ' loading';
    }

    if (state.slideshows[this.props.id] &&
      state.slideshows[this.props.id].transitioning) {
      transitioningClass = ' transitioning';
    }

    const className = 'component-slideshow' +
      firstSlideLoadedClass +
      loadingClass +
      transitioningClass;

    return (
      <div className={className}>
        {slides}
      </div>
    );
  }
}


/**
 * Prop types
 * @prop {Boolean} active - If false, stops the slideshow from transitioning
 * @prop {Array} data - An array of media objects
 * @prop {Function} dispatch - A Redux function to dispatch events
 * @prop {Function} firstSlideLoadCallback - A callback for when the first
 * slide is loaded
 * @prop {Number} id - A unique id for the component
 * @prop {Number} largeBreakpoint - The screen width at which the large image
 * size should be shown
 * @prop {Object} mediaLabCardFrameworkState - The state of the
 * container component
 * @prop {Object} smallCardFrameworkState - The state of the
 * SmallCardFramework component
 * @prop {Object} slideshowState- The state of the SlideShow component
 */
Slideshow.propTypes = {
  active: React.PropTypes.bool,
  data: React.PropTypes.array.isRequired,
  dispatch: React.PropTypes.func,
  firstSlideLoadCallback: React.PropTypes.func,
  id: React.PropTypes.number.isRequired,
  largeBreakpoint: React.PropTypes.number.isRequired,
  mediaLabCardFrameworkState: React.PropTypes.object.isRequired,
  smallCardFrameworkState: React.PropTypes.object.isRequired,
  slideshowState: React.PropTypes.object.isRequired,
};


export default connect(({ mediaLabCardFrameworkState, smallCardFrameworkState, slideshowState }) =>
  ({ mediaLabCardFrameworkState, smallCardFrameworkState, slideshowState }))(Slideshow);



/** WEBPACK FOOTER **
 ** ./assets/js/src/components/Slideshow.jsx
 **/